import queryString from 'query-string';
import { getReferrerCampaign } from '../../../shared/helpers';
import { ABTestGroups } from '../../../shared/services/analytics/analytics';
import * as Fields from '../../../components/Account/Data/Fields';
import { History } from 'history';

export const initAccountValues = ({
  history,
  initField,
  setInitComplete,
  setFirstStageSubmitted,
}: {
  history: History;
  defaultCountry?: string;
  setInitComplete?: () => void;
  setFirstStageSubmitted?: () => void;
  initField: ({ name, value }: Record<string, any>) => void;
}) => {
  const queryParams = queryString.parse(history.location.search) as {
    form?: ABTestGroups;
    referrer?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    agreePrivacyPolicy?: string;
  };

  if (queryParams && queryParams.referrer) {
    initField({
      autoFocus: false,
      className: 'form__input',
      disabled: false,
      id: 'referrer',
      name: 'referrer',
      type: 'referrer',
      value: queryParams.referrer,
    });
    initField({
      name: 'referrerToggle',
      value: true,
    });

    queryParams.referrer = undefined;
    history.replace({ search: queryString.stringify(queryParams) });
  }

  if (queryParams.firstName) {
    initField({
      id: 'firstName',
      name: 'firstName',
      type: 'text',
      value: queryParams.firstName,
    });
    queryParams.firstName = undefined;
    history.replace({ search: queryString.stringify(queryParams) });
  }

  if (queryParams.email) {
    initField({
      name: 'email',
      type: 'text',
      value: queryParams.email,
    });
    queryParams.email = undefined;
    history.replace({ search: queryString.stringify(queryParams) });
  }

  if (queryParams.agreePrivacyPolicy === 'true') {
    initField({
      name: Fields.privacyPolicy.props.name,
      value: true,
    });
    queryParams.agreePrivacyPolicy = undefined;
    history.replace({ search: queryString.stringify(queryParams) });
  }

  if (queryParams.lastName) {
    initField({
      id: 'lastName',
      name: 'lastName',
      type: 'text',
      value: queryParams.lastName,
    });
    queryParams.lastName = undefined;
    history.replace({ search: queryString.stringify(queryParams) });
  }

  const state = history.location.state as
    | undefined
    | {
        email?: string;
        password?: string;
        agreePrivacyPolicy?: boolean;
      };

  if (state?.email) {
    initField({
      id: 'email',
      name: 'email',
      type: 'email',
      value: state.email,
    });
  }

  if (state?.password) {
    initField({
      id: 'password',
      name: 'password',
      type: 'password',
      value: state.password,
    });
  }

  if (state?.[Fields.privacyPolicy.props.name]) {
    initField({
      id: Fields.privacyPolicy.props.id,
      name: Fields.privacyPolicy.props.name,
      value: state?.[Fields.privacyPolicy.props.name],
    });
  }

  if (state?.email && state?.password && state?.[Fields.privacyPolicy.props.name]) {
    setFirstStageSubmitted?.();
  }

  setInitComplete?.();
};

export const setDataToSubmit = (props: { dataFields: any; competitionId?: string; competitionExists: boolean }) => {
  const { dataFields, competitionId, competitionExists } = props;

  const referrer = dataFields.getIn(['referrer', 'value']);
  let employment = null;

  if (dataFields.getIn(['presently_employed', 'value'])) {
    const exposureStatus = dataFields.getIn(['exposureStatus', 'value']) === true ? dataFields.getIn(['exposureStatus', 'value']) : false;

    const employerServices =
      dataFields.getIn(['employerServices', 'value']) !== '' ? dataFields.getIn(['employerServices', 'value']) : null;

    const exposureDetails = dataFields.getIn(['exposureDetails', 'value']) !== '' ? dataFields.getIn(['exposureDetails', 'value']) : null;

    employment = {
      employer: dataFields.getIn(['employer', 'value']),
      business: dataFields.getIn(['business', 'value']),
      title: dataFields.getIn(['title', 'value']),
      description: dataFields.getIn(['description', 'value']),
      nonPublicInformation: dataFields.getIn(['nonPublicInformation', 'value']),
      participationPermitted: dataFields.getIn(['participationPermitted', 'value']),
      finance: dataFields.getIn(['finance', 'value']),
      investmentAdvisory: dataFields.getIn(['investmentAdvisory', 'value']),
      brokerage: dataFields.getIn(['brokerage', 'value']),
      fundamentalResearch: dataFields.getIn(['fundamentalResearch', 'value']),
      proprietaryTrading: dataFields.getIn(['proprietaryTrading', 'value']),
      serviceEngagement: dataFields.getIn(['serviceEngagement', 'value']),
      employerServices,
      exposureStatus,
      exposureDetails,
    };
  }

  let agreementsAccepted = dataFields.getIn(['agree', 'value'])
    ? ['terms-conditions', 'privacy-policy', 'user-agreement', 'account-details', 'challenge']
    : [];

  // Add competition agreement if exists and if is valid
  agreementsAccepted =
    dataFields.getIn(['agreeCompetition', 'value']) && dataFields.getIn(['agreeCompetition', 'valid']) && competitionId
      ? agreementsAccepted.concat([competitionId])
      : agreementsAccepted;

  let auxiliary: any = {
    password: dataFields.getIn(['password', 'value']),
    // set confirmation to password value for wizard form because it has only one password field
    confirmation: dataFields.getIn(['confirmation', 'value']) || dataFields.getIn(['password', 'value']),
    agree: agreementsAccepted,
    captcha: dataFields.getIn(['captcha', 'value']),
  };
  const campaign = getReferrerCampaign(
    // @ts-ignore
    referrer,
    competitionId,
    competitionExists,
    dataFields.getIn(['agreeCompetition', 'value']),
    dataFields.getIn(['agreeCompetition', 'valid'])
  );

  auxiliary = campaign ? { ...auxiliary, campaign: campaign } : auxiliary;

  return {
    email: dataFields.getIn(['email', 'value']),
    firstName: dataFields.getIn(['firstName', 'value']),
    lastName: dataFields.getIn(['lastName', 'value']),
    fullName: dataFields.getIn(['fullName', 'value']),
    publicName: dataFields.getIn(['publicName', 'value']),
    publicPhoto: dataFields.getIn(['publicPhoto', 'value']),
    gender: dataFields.getIn(['gender', 'value']),
    telephone: dataFields.getIn(['telephone', 'value']),
    education: {
      university: dataFields.getIn(['university', 'value']),
    },
    auxiliary,
    address: {
      country: dataFields.getIn(['country', 'value']),
    },
    employment,
  };
};
